<script lang="ts">
  import RangeSlider from "svelte-range-slider-pips";
  import Card from "../atoms/Card.svelte";
  import EstimationIcons from "../atoms/EstimationIcons.svelte";
  import Heading from "../atoms/Heading.svelte";
  import NumberInput from "../atoms/NumberInput.svelte";
  import Text from "../atoms/Text.svelte";
  import Checkbox from "../atoms/Checkbox.svelte";
  import type { ResidenceTypes } from "../../helpers/types";
  import { useDebounce } from "../../helpers/hooks";
  import ErrorText from "../atoms/ErrorText.svelte";

  export let inputEstimationValue: number;
  export let residenceType: ResidenceTypes | undefined;
  export let residenceText: string = "Jag bor i ";
  export let hasPressedContinue: boolean = false;

  const getEnergyEstimationFactor = () => {
    return residenceType === "apartment" ? 50 : 135;
  };
  let values = [inputEstimationValue / getEnergyEstimationFactor()];

  const getTooltipclass = (): Element => {
    return document.querySelector(".rangeFloat");
  };

  const hideSquareMeters = useDebounce(() => {
    const tooltip = getTooltipclass();
    tooltip.classList.add("fade-out");
  }, 1500);

  const onDrag = (e) => {
    const tooltip = getTooltipclass();
    tooltip.classList.remove("fade-out");
    inputEstimationValue = e.detail.value * getEnergyEstimationFactor();
    hideSquareMeters();
  };

  const onNumberInput = (numberInput: Event) => {
    const tooltip = getTooltipclass();
    tooltip.classList.add("fade-out");
    values[0] = numberInput.target.value / getEnergyEstimationFactor();
  };

  const getLabel = (e: number) => {
    const label = residenceType === "apartment" ? "Lägenhet" : "Villa";

    return `${label} ${e} kvm`;
  };

  const onResidenceChange = (type: ResidenceTypes) => {
    const tooltip = getTooltipclass();
    tooltip.classList.remove("fade-out");
    residenceType = type;
    if (residenceType === "apartment") {
      const apartmentValue = 65;
      values[0] = apartmentValue;
      inputEstimationValue = apartmentValue * getEnergyEstimationFactor();
    } else if (residenceType === "house") {
      const houseValue = 200;
      values[0] = houseValue;
      inputEstimationValue = houseValue * getEnergyEstimationFactor();
    }
  };

  $: if (inputEstimationValue > 40500) {
    inputEstimationValue = 40500;
  }
</script>

<Card>
  <slot />
  <Heading class="mb-0"><slot name="heading" /></Heading>
  <Text><slot name="text" /></Text>
  {#if !residenceType && hasPressedContinue}
    <ErrorText>Vänligen välj bostadstyp för att fortsätta.</ErrorText>
  {/if}
  <div class="flex space-x-8 mt-2">
    <Text class="my-auto" variant="primary">{residenceText}</Text>
    <Checkbox
      required={!residenceType}
      onChange={() => onResidenceChange("apartment")}
      checked={residenceType === "apartment"}>Lägenhet</Checkbox
    >
    <Checkbox
      required={!residenceType}
      onChange={() => onResidenceChange("house")}
      checked={residenceType === "house"}>Villa</Checkbox
    >
  </div>
  <div
    class="transition-maxHeight {residenceType
      ? 'max-h-96 duration-700'
      : 'max-h-0  duration-300'} overflow-hidden"
    aria-expanded={residenceType ? true : false}
  >
    <div class="flex fade-in flex-col-reverse md:flex-row-reverse mt-12">
      <div class="mt-6 md:mt-0 self-start items-center">
        <NumberInput
          onInput={onNumberInput}
          class="w-20"
          name="estimation"
          id="estimation"
          bind:value={inputEstimationValue}
          max={35000}
        />
        <label for="estimation" class="text-Neutral900 text-M ml-2">kWh/år</label>
      </div>
      <div class="w-full mt-3 notranslate">
        <RangeSlider
          on:change={onDrag}
          id="slider"
          min={residenceType === "house" ? 60 : 10}
          step={1}
          formatter={(v: any) => getLabel(v)}
          max={residenceType === "house" ? 300 : 250}
          bind:values
          float
        />
        <EstimationIcons {residenceType} />
      </div>
    </div>
  </div>
</Card>
